import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { supabase, PROOF_PHOTOS_BUCKET } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";
import { compressImage } from "../../lib/image";
import {
  enqueue,
  flushOutbox,
  listPending,
  subscribeToOutbox,
  type PendingCheckIn,
} from "../../lib/outbox";
import { requestOutboxSync } from "../../lib/pwa";
import type { CheckIn } from "../../lib/database.types";

export interface FeedEntry extends CheckIn {
  goal_title: string;
  goal_cadence: "daily" | "weekly";
  owner_name: string;
  owner_emoji: string;
  /** True while the check-in is still sitting in the local outbox. */
  pending?: boolean;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

async function fetchFeed(limit = 60): Promise<FeedEntry[]> {
  // The profiles embed names its foreign key: check_in_likes/comments also link
  // check_ins to profiles, which makes a bare `profiles` ambiguous.
  // One embedded select: check_ins -> goals -> profiles, all real tables with
  // real foreign keys, so PostgREST can resolve it in a single round trip.
  const { data, error } = await supabase
    .from("check_ins")
    .select(
      `id, goal_id, owner_id, note, photo_path, checked_in_at,
       goals!inner ( title, cadence ),
       profiles!check_ins_owner_id_fkey!inner ( display_name, avatar_emoji )`,
    )
    .order("checked_in_at", { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (
    data as unknown as Array<
      CheckIn & {
        goals: { title: string; cadence: "daily" | "weekly" };
        profiles: { display_name: string; avatar_emoji: string };
      }
    >
  ).map((row) => ({
    id: row.id,
    goal_id: row.goal_id,
    owner_id: row.owner_id,
    note: row.note,
    photo_path: row.photo_path,
    checked_in_at: row.checked_in_at,
    goal_title: row.goals.title,
    goal_cadence: row.goals.cadence,
    owner_name: row.profiles.display_name,
    owner_emoji: row.profiles.avatar_emoji,
  }));
}

export function useFeed() {
  return useQuery({ queryKey: keys.feed, queryFn: () => fetchFeed() });
}

/** The signed-in user's check-ins since local midnight, newest first. */
export function useTodayCheckIns(userId: string | undefined) {
  return useQuery({
    queryKey: [...keys.checkIns, "today", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const midnight = new Date();
      midnight.setHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("check_ins")
        .select("id, goal_id, photo_path, checked_in_at")
        .eq("owner_id", userId!)
        .gte("checked_in_at", midnight.toISOString())
        .order("checked_in_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useGoalCheckIns(goalId: string | undefined) {
  return useQuery({
    queryKey: keys.checkInsByGoal(goalId ?? ""),
    enabled: Boolean(goalId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("check_ins")
        .select("*")
        .eq("goal_id", goalId!)
        .order("checked_in_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// The outbox, exposed to React
// ---------------------------------------------------------------------------

let cachedPending: PendingCheckIn[] = [];
let pendingVersion = 0;

function subscribePending(callback: () => void) {
  const unsubscribe = subscribeToOutbox(() => {
    void listPending().then((rows) => {
      cachedPending = rows;
      pendingVersion += 1;
      callback();
    });
  });

  // Prime the cache on first subscribe.
  void listPending().then((rows) => {
    cachedPending = rows;
    pendingVersion += 1;
    callback();
  });

  return unsubscribe;
}

const getPendingSnapshot = () => pendingVersion;

/** Check-ins waiting to reach the server on this device. */
export function usePendingCheckIns(): PendingCheckIn[] {
  useSyncExternalStore(subscribePending, getPendingSnapshot, () => 0);
  return cachedPending;
}

// ---------------------------------------------------------------------------
// Writing a check-in
// ---------------------------------------------------------------------------

export interface CheckInDraft {
  goalId: string;
  goalTitle: string;
  note?: string;
  photo?: File | null;
}

/**
 * Record a check-in.
 *
 * Always writes to the local outbox first, then tries to flush. That ordering
 * is what makes the "never silently lost" guarantee hold: if the tab dies
 * between the tap and the response, the check-in is already durable on disk.
 */
export function useCheckIn(ownerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draft: CheckInDraft) => {
      const id = crypto.randomUUID();
      const photo = draft.photo ? await compressImage(draft.photo) : null;

      await enqueue({
        id,
        goal_id: draft.goalId,
        owner_id: ownerId,
        goal_title: draft.goalTitle,
        note: draft.note?.trim() || null,
        photo,
        checked_in_at: new Date().toISOString(),
      });

      const delivered = await flushOutbox();
      if (delivered === 0) {
        // Still queued. Ask the browser to wake us when the network returns.
        void requestOutboxSync();
      }
      return { id, queued: delivered === 0 };
    },

    // Optimistic: the bar moves and the streak ticks before the network answers.
    onMutate: async (draft) => {
      await queryClient.cancelQueries({ queryKey: keys.goals });
      const previous = queryClient.getQueryData(keys.goals);

      queryClient.setQueryData(
        keys.goals,
        (
          goals:
            | Array<{
                id: string;
                stats: {
                  period_count: number;
                  target_per_period: number;
                  period_complete: boolean;
                  current_streak: number;
                } | null;
              }>
            | undefined,
        ) =>
          goals?.map((goal) => {
            if (goal.id !== draft.goalId || !goal.stats) return goal;
            const nextCount = goal.stats.period_count + 1;
            const nowComplete = nextCount >= goal.stats.target_per_period;
            return {
              ...goal,
              stats: {
                ...goal.stats,
                period_count: nextCount,
                period_complete: nowComplete,
                current_streak:
                  nowComplete && !goal.stats.period_complete
                    ? goal.stats.current_streak + 1
                    : goal.stats.current_streak,
              },
            };
          }),
      );

      return { previous };
    },

    onError: (_error, _draft, context) => {
      if (context?.previous)
        queryClient.setQueryData(keys.goals, context.previous);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.feed });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
      void queryClient.invalidateQueries({ queryKey: keys.weekPeriods });
      void queryClient.invalidateQueries({ queryKey: keys.checkIns });
    },
  });
}

export function useUndoCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (checkIn: { id: string; photo_path: string | null }) => {
      if (checkIn.photo_path) {
        await supabase.storage
          .from(PROOF_PHOTOS_BUCKET)
          .remove([checkIn.photo_path]);
      }
      const { error } = await supabase
        .from("check_ins")
        .delete()
        .eq("id", checkIn.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.goals });
      void queryClient.invalidateQueries({ queryKey: keys.feed });
      void queryClient.invalidateQueries({ queryKey: keys.recap });
      void queryClient.invalidateQueries({ queryKey: keys.weekPeriods });
      void queryClient.invalidateQueries({ queryKey: keys.checkIns });
    },
  });
}

// ---------------------------------------------------------------------------
// Proof photos
// ---------------------------------------------------------------------------

/**
 * The bucket is private, so images need a signed URL. One hour is long enough
 * for a feed session and short enough that a leaked link is harmless.
 */
export function usePhotoUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ["photo", path],
    enabled: Boolean(path),
    staleTime: 45 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(PROOF_PHOTOS_BUCKET)
        .createSignedUrl(path!, 60 * 60);
      if (error) throw error;
      return data.signedUrl;
    },
  });
}
